import { queryOptions } from '@tanstack/react-query'
import { apiExchange, apiGet, apiPost, type PostValue } from './client'

/**
 * Settings -> Agent Install, calea noua.
 *
 * Perechile vechi, din `application/controllers/settings/Download_agent.php`:
 *
 *   index             -> GET  /settings/download_agent
 *   enroll_key_create -> POST /settings/download_agent/key
 *   enroll_key_view   -> POST /settings/download_agent/key/view
 *   enroll_key_limits -> POST /settings/download_agent/key/limits
 *
 * "View key" e POST fiindca scoate un secret din baza SI scrie un rand in
 * jurnal - un GET s-ar tine in cache si ar ajunge in istoricul browserului.
 *
 * CHEIA NU VINE LA DESCHIDERE. Ecranul stie doar daca EXISTA una (`hasKey`);
 * textul ei se cere apasat, si cererea aia lasa urma in `it_auth_events`.
 *
 * Comenzile de instalare se scriu pe SERVER, nu aici: in portal se lipeau in
 * JavaScript, in macheta.
 */

export type DownloadAgentTab = {
  id: string
  label: string
  icon: string
  href: string
  right: string
  group: string
}

/** Cele patru comenzi; sirul gol inseamna "nu se poate scrie inca". */
export type InstallCommands = {
  linux: string
  winserver: string
  windows: string
  mac: string
}

export type AgentVersions = {
  latest: string
  linux: string
  winserver: string
  windows: string
  mac: string
}

export type DownloadAgentShell = {
  pageTitle: string
  moduleUrl: string
  tabs: DownloadAgentTab[]
  companyName: string
  hasKey: boolean
  /** Cheia de acum chiar se poate arata: are copie cifrata. Cele mai vechi decat pastrarea copiei nu se mai pot, doar inlocui. */
  canShowKey: boolean
  /** `settings.my_company.edit`: a face cheia, A O VEDEA, si limitele. */
  canWrite: boolean
  /** Firma n-are cheie si omul n-are drept sa o faca: trebuie cerut unui admin. */
  needsAdmin?: boolean
  /** Prima cheie, facuta singura la deschiderea ecranului; vine o singura data. */
  autoKey?: string | null
  apiUrl: string
  /** Instalatorul clasic 7.x; gol cand adresa nu se stie - atunci cardul nu se deseneaza. */
  workstationUrl: string
  limits: RawLimits
  versions: AgentVersions
  commands: InstallCommands
}

/**
 * Termenul, plafonul si numaratoarea cheii, cu numele coloanelor din `firma`.
 *
 * `available: false` inseamna "instalarea asta nu poate tine limite" (coloanele
 * nu sunt migrate), NU "nu sunt limite puse".
 */
export type RawLimits = {
  available: boolean
  expires_at: string | null
  max_uses: number | null
  uses: number
}

export type KeyAnswer = {
  key: string
  limits: RawLimits
  commands: InstallCommands
}

export const downloadAgentShellQuery = queryOptions({
  queryKey: ['settings', 'download-agent'],
  queryFn: () => apiGet<DownloadAgentShell>('/settings/download_agent'),
})

/** Cheie noua; cea dinainte se opreste pe loc pentru instalari noi. */
export function createEnrollKey() {
  return apiPost<KeyAnswer>('/settings/download_agent/key', {})
}

/** Cheia de acum. Fiecare vedere se scrie in jurnal. */
export function viewEnrollKey() {
  return apiPost<KeyAnswer>('/settings/download_agent/key/view', {})
}

export function saveEnrollKeyLimits(fields: Record<string, PostValue>) {
  return apiPost<{ saved: boolean; limits: RawLimits }>('/settings/download_agent/key/limits', fields)
}

/** Numele fisierului, cand serverul nu-l spune in `Content-Disposition`. */
export const GPO_FILENAME = 'od-gpo-deploy.ps1'

/**
 * Pachetul de instalare in masa prin Group Policy, salvat pe disc.
 *
 * POST, si nu un `<a href>`: fisierul POARTA CHEIA firmei in clar, exact ca
 * raspunsul lui "View key", si fiecare descarcare se scrie in jurnal. Un GET
 * ar sta in istoricul browserului si s-ar putea preincarca - un secret
 * descarcat de nimeni, notat pe numele cuiva.
 */
export async function downloadGpoPackage(): Promise<string> {
  const out = await apiExchange('/settings/download_agent/gpo', { method: 'POST' })
  const name = out.filename || GPO_FILENAME
  const href = URL.createObjectURL(new Blob([out.bytes], { type: 'text/plain;charset=utf-8' }))
  const link = document.createElement('a')
  link.href = href
  link.download = name
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(href)

  return name
}
